import React, { useState, useEffect } from 'react';
import agentApiService from '../../services/agentApiService';
import './TokenHistory.css';

const TokenHistory = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredHistory, setFilteredHistory] = useState([]);

  useEffect(() => {
    fetchTokenHistory();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredHistory(history);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = history.filter(item =>
        (item.transactionId && item.transactionId.toLowerCase().includes(term)) ||
        (item.transactionType && item.transactionType.toLowerCase().includes(term)) ||
        (item.reason && item.reason.toLowerCase().includes(term)) ||
        (item.source && item.source.toLowerCase().includes(term)) ||
        (item.performedByName && item.performedByName.toLowerCase().includes(term)) ||
        (item.transactionDate && item.transactionDate.toLowerCase().includes(term))
      );
      setFilteredHistory(filtered);
    }
  }, [history, searchTerm]);

  const fetchTokenHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📜 Fetching token audit ledger history...');
      const response = await agentApiService.getTokenRechargeHistory(0, 100);
      
      if (response.success) {
        setHistory(response.history || []);
      } else {
        throw new Error(response.message || 'Failed to fetch token history');
      }
    } catch (err) {
      console.error('Error fetching token history:', err);
      setError(err.message || 'Failed to load token history');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeBadge = (type) => {
    switch (type) {
      case 'DEFAULT_ALLOCATION':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800"><i className="fas fa-user-check mr-1"></i> Default Allocation</span>;
      case 'BOOKING_REWARD':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800"><i className="fas fa-gift mr-1"></i> Booking Reward</span>;
      case 'EXTRA_TOKENS_GRANTED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-800"><i className="fas fa-coins mr-1"></i> Support/Admin Grant</span>;
      case 'USAGE_DEDUCTION':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-minus-circle mr-1"></i> Usage Deduction</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-800">{type || 'TRANSACTION'}</span>;
    }
  };

  if (loading) {
    return (
      <div className="token-history-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading token audit ledger...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-history-container">
        <div className="error-message">
          <h3>Error Loading Token History</h3>
          <p>{error}</p>
          <button onClick={fetchTokenHistory} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="token-history-container">
      {/* Header */}
      <div className="page-header">
        <h1>Token Audit Ledger</h1>
        <p>Complete transaction history and audit trail of your account tokens</p>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by Transaction ID, Type, Reason, or Source..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="history-table-container">
        <div className="table-card">
          <div className="table-header">
            <h3>Token Transaction History</h3>
            <div className="table-info">
              Showing {filteredHistory.length} audit records
            </div>
          </div>
          
          <div className="table-wrapper overflow-x-auto">
            <table className="history-table w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase">Txn ID</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase">Date & Time</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase">Type</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase text-center">Tokens</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase text-center">Prev Balance</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase text-center">New Balance</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase">Reason</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase">Source</th>
                  <th className="p-3 text-xs font-bold text-gray-700 uppercase">Performed By</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length > 0 ? (
                  filteredHistory.map((item, index) => (
                    <tr key={item.transactionId || item.tokenId || index} className="border-b hover:bg-gray-50 text-sm">
                      <td className="p-3 font-mono text-xs text-gray-900">{item.transactionId || item.tokenId || 'N/A'}</td>
                      <td className="p-3 text-xs text-gray-600 whitespace-nowrap">{item.transactionDate || item.rechargedDate || 'N/A'}</td>
                      <td className="p-3">{getTransactionTypeBadge(item.transactionType)}</td>
                      <td className="p-3 text-center font-bold text-green-700">
                        {item.amount != null ? (item.amount > 0 ? `+${item.amount}` : item.amount) : (item.noOfTokensRecharge || '+20')}
                      </td>
                      <td className="p-3 text-center font-medium text-gray-600">{item.previousBalance ?? 'N/A'}</td>
                      <td className="p-3 text-center font-bold text-blue-900">{item.balanceAfter ?? 'N/A'}</td>
                      <td className="p-3 text-gray-800 max-w-xs truncate" title={item.reason}>{item.reason || 'N/A'}</td>
                      <td className="p-3 text-xs font-semibold text-gray-600">{item.source || 'SYSTEM'}</td>
                      <td className="p-3 text-xs text-gray-700">{item.performedByName || item.performedBy || 'System'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="p-8 text-center text-gray-500">
                      {searchTerm ? 'No matching audit records found' : 'No token transaction history available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenHistory;